import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { DEFAULT_SHOWCASE_ROOM } from '@store/constants'
import { RootStateTypeExtra } from '@store/types'

export interface AppConfigInterface {
  remoteRefreshTriggerKey: string
  offlineMode: boolean
  firebaseHost: string
  firebasePort: string
  room: string
  precinctLabel: string
  fullscreen: boolean
  defaultAgent: boolean
  isProjectSwitchInitiated: boolean
}

const LOCAL_STORAGE_KEY = 'appConfig'

const defaultAppConfig = {
  remoteRefreshTriggerKey: '',
  offlineMode: false,
  firebaseHost: '',
  firebasePort: '',
  room: '',
  precinctLabel: '',
  fullscreen: false,
  defaultAgent: false,
  isProjectSwitchInitiated: false,
}

let initialState = defaultAppConfig as AppConfigInterface

const appConfig = localStorage.getItem(LOCAL_STORAGE_KEY)

if (appConfig) {
  initialState = JSON.parse(appConfig)
}

export const saveAppConfigToStorage = (appConfig: AppConfigInterface) => {
  const updatedAppConfig: Partial<AppConfigInterface> = {
    ...appConfig,
  }

  delete updatedAppConfig.isProjectSwitchInitiated

  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedAppConfig))
  return updatedAppConfig
}

export const saveRoomToStorage = (
  appConfig: AppConfigInterface,
  room: string
) => {
  const updatedAppConfig: AppConfigInterface = {
    ...appConfig,
    room: room.toLowerCase() === DEFAULT_SHOWCASE_ROOM ? '' : room,
  }
  saveAppConfigToStorage(updatedAppConfig)
  return updatedAppConfig
}

export const appConfigSlice = createSlice({
  name: 'appConfig',
  initialState,
  reducers: {
    setRemoteRefreshTriggerKey: (
      state: AppConfigInterface,
      action: PayloadAction<string>
    ) => {
      const newState = {
        ...state,
        ...{ remoteRefreshTriggerKey: action.payload },
      }
      saveAppConfigToStorage(newState)
      return newState
    },
    setOfflineMode: (
      state: AppConfigInterface,
      action: PayloadAction<boolean>
    ) => {
      const newState = {
        ...state,
        ...{ offlineMode: action.payload },
      }
      saveAppConfigToStorage(newState)
      return newState
    },
    setFirebaseHost: (
      state: AppConfigInterface,
      action: PayloadAction<string>
    ) => {
      const newState = {
        ...state,
        ...{ firebaseHost: action.payload },
      }
      saveAppConfigToStorage(newState)
      return newState
    },
    setFirebasePort: (
      state: AppConfigInterface,
      action: PayloadAction<string>
    ) => {
      const newState = {
        ...state,
        ...{ firebasePort: action.payload },
      }
      saveAppConfigToStorage(newState)
      return newState
    },
    setRoom: (state: AppConfigInterface, action: PayloadAction<string>) => {
      const newState = saveRoomToStorage(state, action.payload)
      return {
        ...state,
        ...newState,
      }
    },
    setFullScreen: (
      state: AppConfigInterface,
      action: PayloadAction<boolean>
    ) => {
      const newState = {
        ...state,
        ...{ fullscreen: action.payload },
      }
      saveAppConfigToStorage(newState)
      return newState
    },
    setPrecinctLabel: (
      state: AppConfigInterface,
      action: PayloadAction<string>
    ) => {
      const newState = {
        ...state,
        ...{ precinctLabel: action.payload },
      }
      saveAppConfigToStorage(newState)
      return newState
    },
    setDefaultAgent: (state, action: PayloadAction<boolean>) => {
      const newState = {
        ...state,
        ...{ defaultAgent: action.payload },
      }
      saveAppConfigToStorage(newState)
      return newState
    },
    setIsProjectSwitchInitiated: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isProjectSwitchInitiated: action.payload,
    }),
    resetAppConfig: () => {
      localStorage.removeItem(LOCAL_STORAGE_KEY)
      return defaultAppConfig
    },
  },
})

export const {
  setRemoteRefreshTriggerKey,
  setOfflineMode,
  setFirebaseHost,
  setFirebasePort,
  setRoom,
  setFullScreen,
  setPrecinctLabel,
  setDefaultAgent,
  setIsProjectSwitchInitiated,
  resetAppConfig,
} = appConfigSlice.actions

export const selectAppConfig = (state: RootStateTypeExtra) => state.appConfig

export const selectCurrentRoom = (state: RootStateTypeExtra) => {
  const room = state.appConfig?.room || DEFAULT_SHOWCASE_ROOM
  const isDefault = room.toLowerCase() === DEFAULT_SHOWCASE_ROOM

  if (isDefault) {
    return DEFAULT_SHOWCASE_ROOM
  }

  return room
}

export const selectIsProjectSwitchInitiated = (state: RootStateTypeExtra) =>
  !!state.appConfig.isProjectSwitchInitiated

export default appConfigSlice.reducer
